(ns common.form
  "New form abstraction using react-hook-form"
  (:require ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/FormControl$default" :as FormControl]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/FormHelperText$default" :as FormHelperText]
            ["@mui/material/InputLabel$default" :as InputLabel]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Radio$default" :as Radio]
            ["@mui/material/RadioGroup$default" :as RadioGroup]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Switch$default" :as Switch]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/x-date-pickers/DatePicker" :refer [DatePicker]]
            ["@mui/x-date-pickers/DateTimePicker" :refer [DateTimePicker]]
            ["@mui/x-date-pickers/TimePicker" :refer [TimePicker]]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj ->js bean]]
            [clojure.spec.alpha :as spec]
            [common.util :refer [spread-obj spread-props]]
            [common.widgets.asset-upload :as asset-upload]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.form-field :refer [form-field hint]]
            [malli.core :as m]
            [malli.error :as me]
            [phrase.alpha :as p]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defhook defui]]))

;; React Hook Form "embraces" uncontrolled inputs, but with MUI we
;; probably want controlled fields.
;; TODO: How to debug current form value?

(defn use-form
  "https://react-hook-form.com/docs/useform

  The props is a JS object.
  Returns cljs-bean (shallow) value that can be destructured."
  [props]
  (bean (rhf/useForm props)))

(defn use-field-array
  [props]
  (bean (rhf/useFieldArray props)))

(defn use-form-context
  []
  (bean (rhf/useFormContext)))

(defhook use-form-callback
  [form-control callback {:keys [change-only]}]
  (let [callback-effect-event (uix/use-effect-event callback)
        js-form-control (->js form-control)]
    (uix/use-effect
     (fn [] (let [{:keys [watch]} (->clj js-form-control)
                  subscription (watch
                                (fn [data details]
                                  (when (or (not change-only)
                                            (= (.-type details) "change"))
                                    (callback-effect-event (->clj data) (->clj details)))))]
              #(.unsubscribe subscription)))
     [js-form-control change-only])))

(defn error-message
  "Takes fieldState error object and creates a readable message for it."
  [error]
  (or (when (seq (:message error))
        (:message error))
      (case (:type error)
        "required" "Missing value"
        "minLength" "Too short value"
        "maxLength" "Too long value"
        nil)))

(defui form-text-field
  "Form field connecting MUI TextField to Form as a controlled field."
  [{:keys [label field-props on-change-fn controller-props]
    :or {on-change-fn identity}
    :as props}]
  ($ rhf/Controller
     (spread-props
      (select-keys props [:name :control :rules])
      {:render (fn [^js props]
                 (let [{{:keys [onChange value]} :field
                        {:keys [error]} :fieldState
                        :keys [field] :as props} (->clj props)]
                   ($ TextField
                      (spread-props
                       field
                       {:value (str value)
                        :onChange #(-> % .-target .-value on-change-fn onChange)
                        :helperText (error-message error)
                        :error (boolean error)
                        :label label}
                       (if (fn? field-props)
                         (field-props props)
                         field-props)))))}
      controller-props)))

(defui autocomplete-field
  "This uses the `dropdown` widget, which doesn't support multiple free-solo"
  [{:keys [label field-props on-after-change text-field-props controller-props] :as props}]
  ($ rhf/Controller
     (spread-props
      (select-keys props [:name :control :rules])
      {:render (fn [^js props]
                 (let [{{:keys [error]} :fieldState
                        {:keys [onChange disabled] :as field} :field
                        :as props} (->clj props)]
                   (r/as-element
                    [dropdown
                     (merge field
                            {:error (boolean error)
                             :disabled? (boolean disabled)
                             :text-field-props (merge {:helperText (:message error)
                                                       :label label}
                                                      text-field-props)
                             :on-change (fn [new-value]
                                          (onChange (->js new-value))
                                          (when on-after-change (on-after-change)))}
                            (if (fn? field-props)
                              (field-props props)
                              field-props))])))}
      controller-props)))

(defui select-field
  "Form field connecting MUI Select to Form as a controlled field."
  [{:keys [label name field-props form-control-props controller-props] :as props}]
  ($ rhf/Controller
     (spread-props (select-keys props [:name :control :rules])
                   {:render (fn [^js props]
                              (let [x (->clj props)
                                    error (-> x :fieldState :error)
                                    field-props* (if (fn? field-props)
                                                   (field-props x)
                                                   field-props)
                                    options (:options field-props*)
                                    label-id (str name "-input-label")]
                                ($ FormControl (spread-props {:error (boolean error)}
                                                             form-control-props)
                                   ($ InputLabel {:id label-id} label)
                                   ($ Select
                                      (spread-props (:field x)
                                                    {:label label
                                                     :labelId label-id}
                                                    (dissoc field-props* :options))
                                      (for [{:keys [id label disabled]} options]
                                        ($ MenuItem {:key id :value id :disabled disabled} label)))
                                   ($ FormHelperText (error-message error)))))}
                   controller-props)))

(defui checkbox
  "Form field connecting MUI Checkbox to Form as a controlled field."
  [{:keys [label field-props controller-props] :as props}]
  ($ rhf/Controller
     (spread-props (select-keys props [:name :control])
                   {:render (fn [^js props]
                              (let [{{:keys [value onChange]} :field :as x} (->clj props)]
                                ($ FormControlLabel
                                   {:label label
                                    :control
                                    ($ Checkbox
                                       (spread-props
                                        (:field x)
                                        {:label label
                                         :checked value
                                         :onChange #(-> % .-target .-checked onChange)}
                                        (if (fn? field-props)
                                          (field-props x)
                                          field-props)))})))}
                   controller-props)))

(defui radio-group
  "Form field for a group of radio buttons that stores a single selected value.
   Clicking a selected radio button again will deselect it.

   Options should be a collection of maps with :value and :label keys.
   The form value will be the selected value (not a vector).

   Example:
   ($ radio-group
      {:name \"report-type\"
       :control control
       :field-props {:options [{:value \"daily\" :label \"Daily Report\"}
                               {:value \"weekly\" :label \"Weekly Report\"}
                               {:value \"monthly\" :label \"Monthly Report\"}]
                     :row true}})"
  [{:keys [field-props controller-props] :as props}]
  (let [{:keys [options row]} field-props
        options (or options [])]
    ($ rhf/Controller
       (spread-props (select-keys props [:name :control])
                     {:render (fn [^js props]
                                (let [{{:keys [value onChange]} :field
                                       {:keys [error]} :fieldState} (->clj props)]
                                  ($ FormControl {:error (boolean error)}
                                     ($ RadioGroup
                                        {:value (str value)
                                         :onChange #(onChange (-> % .-target .-value))
                                         :row row}
                                        (for [{option-value :value option-label :label} options]
                                          ($ FormControlLabel
                                             {:key (str option-value)
                                              :value (str option-value)
                                              :label option-label
                                              :onClick (fn [e]
                                                         ;; If clicking on already selected value, deselect it
                                                         (when (= (str value) (str option-value))
                                                           (.preventDefault e)
                                                           (onChange "")))
                                              :control ($ Radio)})))
                                     (when error
                                       ($ FormHelperText (error-message error))))))}
                     controller-props))))

(defui switch
  "Form field connecting MUI Switch to Form as a controlled field."
  [{:keys [label field-props on-after-change controller-props] :as props}]
  ($ rhf/Controller
     (spread-props (select-keys props [:name :control])
                   {:render (fn [^js props]
                              (let [{{:keys [value onChange]} :field :as x} (->clj props)]
                                ($ FormControlLabel
                                   {:label label
                                    :sx #js {:ml 0
                                             ".MuiFormControlLabel-label" #js {:ml 1}}
                                    :control
                                    ($ Switch
                                       (spread-props
                                        (:field x)
                                        {:checked (boolean value)
                                         :onChange (fn [e]
                                                     (let [new-value (-> e .-target .-checked)]
                                                       (onChange new-value)
                                                       (when on-after-change (on-after-change))))}
                                        (if (fn? field-props)
                                          (field-props x)
                                          field-props)))})))}
                   controller-props)))

(defui form-date-time-picker
  "Form field connecting MUI DateTimePicker to Form as a controlled field."
  [{:keys [label field-props text-field-props controller-props] :as props}]
  ($ rhf/Controller
     (spread-props
      (select-keys props [:name :control :rules])
      {:render (fn [^js props]
                 (let [{{:keys [onBlur value] :as field-control} :field
                        {:keys [error]} :fieldState
                        :as props} (->clj props)]
                   ($ DateTimePicker
                      (spread-props
                       field-control
                       {:label label
                        :value (if (= js/undefined value) nil value)
                        :slotProps #js {:textField
                                        (spread-obj #js {:onBlur onBlur
                                                         :error (boolean error)}
                                                    (->js text-field-props))}
                        :onClose onBlur
                        :timeSteps #js {:hours 1 :minutes 1}}
                       (if (fn? field-props)
                         (field-props props)
                         field-props)))))}
      controller-props)))

(defui form-time-picker
  "Form field connecting MUI TimePicker to Form as a controlled field."
  [{:keys [label field-props text-field-props controller-props] :as props}]
  ($ rhf/Controller
     (spread-props (select-keys props [:name :control :rules])
                   {:render (fn [^js props]
                              (let [{{:keys [onBlur value] :as field-control} :field
                                     {:keys [error]} :fieldState}
                                    (->clj props)]
                                ($ TimePicker
                                   (spread-props
                                    field-control
                                    {:label label
                                     :value (if (= js/undefined value) nil value)
                                     :slotProps #js {:textField
                                                     (spread-obj #js {:onBlur onBlur
                                                                      :error (boolean error)}
                                                                 (->js text-field-props))}
                                     :onClose onBlur
                                     :format "HH:mm"}
                                    (if (fn? field-props)
                                      (field-props field-control)
                                      field-props)))))}
                   controller-props)))

(defui form-date-picker
  "Form field connecting MUI DatePicker to Form as a controlled field."
  [{:keys [label field-props text-field-props controller-props] :as props}]
  ($ rhf/Controller
     (spread-props (select-keys props [:name :control :rules])
                   {:render (fn [^js props]
                              (let [{{:keys [onBlur value] :as field-control} :field
                                     {:keys [error]} :fieldState}
                                    (->clj props)]
                                ($ DatePicker
                                   (spread-props
                                    field-control
                                    {:label label
                                     :value (if (= js/undefined value) nil value)
                                     :slotProps #js {:textField
                                                     (spread-obj #js {:onBlur onBlur
                                                                      :error (boolean error)}
                                                                 (->js text-field-props))}
                                     :onClose onBlur}
                                    (if (fn? field-props)
                                      (field-props field-control)
                                      field-props)))))}
                   controller-props)))

(defn spec-resolver
  "Adapted from `common.form.core/get-validate-form-fn`"
  [spec]
  (fn [data]
    (let [data-map (->clj data)
          valid? (spec/valid? spec data-map)
          problems (when-not valid?
                     (->> (spec/explain-data spec data-map)
                          ::spec/problems
                          (keep #(p/phrase {} %))))
          grouped (dissoc (group-by :in problems) nil)
          grouped-by-key (dissoc (group-by :key problems) nil)
          multi-error-group (apply merge
                                   (flatten (for [[k v] (group-by :ins problems)]
                                              (when k
                                                (mapv (fn [x] {x v}) k)))))
          errors (->> (merge multi-error-group grouped grouped-by-key)
                      (map (fn [[path [{{:keys [pred]} :problem
                                        :keys [message]}]]]
                             [(name (first path))
                              {:type (str pred)
                               :message message}]))
                      (into {}))]
      #js {:values (if valid? data nil)
           :errors (if valid? #js {} (clj->js errors))})))

(defn arrange-malli-errors [data errors]
  (let [arrange-errors (fn arrange-errors [x]
                         (if (string? (first x)) ; seq of error messages
                           {:type "validation"
                            :message (first x)
                            :messages x}
                           (persistent!
                            (reduce-kv (fn [out k v]
                                         (assoc! out k (arrange-errors v)))
                                       (transient {})
                                       x))))
        valid? (empty? errors)]
    #js {:values (if valid? data #js {})
         :errors (->js (if valid? {} (arrange-errors errors)))}))

(defhook use-malli-resolver
  "React Hook Form resolver using a Malli schema.
   Returns a memoized callback that validates JS form data.

   Note: `schema` must be static to avoid stale validation due to memoization."
  [schema]
  (uix/use-callback
   (fn [data]
     (let [explainer (m/explainer schema)]
       (->> data ->clj explainer me/humanize (arrange-malli-errors data))))
   [schema]))

(defui asset-upload [{:keys [name label path extensions filetype hint-text]}]
  ($ rhf/Controller
     {:name name
      :render (fn [^js props]
                (let [{{:keys [error]} :fieldState
                       {:keys [onChange onBlur]} :field} (->clj props)]
                  ($ form-field
                     {:label label
                      :err-msg (:message error)}
                     ($ asset-upload/upload
                        {:enableEdit false
                         :enablePreview false
                         :resumable? true
                         :error error
                         :onChange (fn [value _] (onChange value) (onBlur))
                         :path path
                         :extensions extensions
                         :filetype filetype})
                     (when hint-text ($ hint hint-text)))))}))

(comment
  ;; Component with register to add props
  ($ TextField
     (spread-props #js {:label "Email"
                        :type "text"
                        :placefolder "foo@example.com"
                        :fullWidth true}
                   (register "email" #js {:required true
                                          :maxLength 10}))))
